import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Freelancer } from "@/types/freelancer";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useFreelancersInvalidate } from "@/hooks/use-freelancers-invalidate";
import { ChevronDown } from "lucide-react";
import { useState, useEffect } from "react";
import { reportUserError } from "@/lib/report-error";

const formSchema = z.object({
  nome: z.string().min(2, "Nome deve ter pelo menos 2 caracteres"),
  email: z.string().email("Email inválido"),
  telefone: z.string().optional(),
  cidade: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  country: z.string().optional(),
  portfolio_url: z.string().optional(),
  linkedin_url: z.string().optional(),
  pretensao_salarial: z.string().optional(),
  historia_profissional: z.string().optional(),
  objetivos: z.string().optional(),
  areas: z.array(z.string()).optional(),
  quality_seal: z.boolean().optional(),
  notes: z.string().optional(),
  is_active: z.boolean().optional(),
  // New fields
  phone_number: z.string().optional(),
  cpf: z.string().optional(),
  rg: z.string().optional(),
  birth_date: z.string().optional(),
  company_name: z.string().optional(),
  zip_code: z.string().optional(),
  cnpj: z.string().optional(),
  bank_name: z.string().optional(),
  agency: z.string().optional(),
  account_number: z.string().optional(),
  pix_key: z.string().optional(),
});

interface FreelancerFormProps {
  freelancer: Freelancer;
  onSuccess?: () => void;
}

export function FreelancerForm({ freelancer, onSuccess }: FreelancerFormProps) {
  const { toast } = useToast();
  const invalidateFreelancers = useFreelancersInvalidate();
  const [isAdditionalInfoOpen, setIsAdditionalInfoOpen] = useState(false);
  // Áreas canônicas ativas — vêm da tabela `areas_atuacao` (mesma fonte que o
  // dropdown de criação de vaga). Garante que os freelancers usem o mesmo
  // vocabulário das vagas, condição pro `overlap` do engine de recomendação
  // funcionar (ver R6.3 em docs/RECOMMENDATIONS-REFINEMENT.md).
  const [areasCanonicas, setAreasCanonicas] = useState<{ id: string; nome: string }[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("areas_atuacao")
        .select("id, nome")
        .eq("is_active", true)
        .order("nome");
      if (error) {
        console.error("Erro ao buscar áreas de atuação:", error);
        return;
      }
      if (!cancelled && data) setAreasCanonicas(data);
    })();
    return () => { cancelled = true; };
  }, []);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      nome: freelancer.nome,
      email: freelancer.email,
      telefone: freelancer.telefone || "",
      cidade: freelancer.cidade || "",
      city: freelancer.city || "",
      state: freelancer.state || "",
      country: freelancer.country || "",
      portfolio_url: freelancer.portfolio_url || "",
      linkedin_url: freelancer.linkedin_url || "",
      pretensao_salarial: freelancer.pretensao_salarial || "",
      historia_profissional: freelancer.historia_profissional || "",
      objetivos: freelancer.objetivos || "",
      areas: freelancer.areas || [],
      quality_seal: freelancer.quality_seal || false,
      is_active: freelancer.is_active !== false, // default true se não especificado
      notes: freelancer.notes || "",
      // New default values
      phone_number: freelancer.phone_number || "",
      cpf: freelancer.cpf || "",
      rg: freelancer.rg || "",
      birth_date: freelancer.birth_date || "",
      company_name: freelancer.company_name || "",
      zip_code: freelancer.zip_code || "",
      cnpj: freelancer.cnpj || "",
      bank_name: freelancer.bank_name || "",
      agency: freelancer.agency || "",
      account_number: freelancer.account_number || "",
      pix_key: freelancer.pix_key || "",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      console.log("Iniciando atualização do freelancer:", freelancer.id);
      console.log("Dados para atualização:", values);

      const updateData = {
        nome: values.nome,
        email: values.email,
        telefone: values.telefone || null,
        cidade: values.cidade || null,
        city: values.city || null,
        state: values.state || null,
        country: values.country || null,
        portfolio_url: values.portfolio_url || null,
        linkedin_url: values.linkedin_url || null,
        pretensao_salarial: values.pretensao_salarial || null,
        historia_profissional: values.historia_profissional || null,
        objetivos: values.objetivos || null,
        areas: values.areas || [],
        quality_seal: values.quality_seal || false,
        notes: values.notes || null,
        is_active: values.is_active !== false,
        // New fields for update
        phone_number: values.phone_number || null,
        cpf: values.cpf || null,
        rg: values.rg || null,
        birth_date: values.birth_date || null,
        company_name: values.company_name || null,
        zip_code: values.zip_code || null,
        cnpj: values.cnpj || null,
        bank_name: values.bank_name || null,
        agency: values.agency || null,
        account_number: values.account_number || null,
        pix_key: values.pix_key || null,
        updated_at: new Date().toISOString()
      };

      console.log("Dados processados para atualização:", updateData);

      // Fazer a atualização sem .select() para evitar problemas
      const { error } = await supabase
        .from("freelancers")
        .update(updateData)
        .eq("id", freelancer.id);

      console.log("Resposta da atualização - error:", error);

      if (error) {
        console.error("Erro ao atualizar freelancer:", error);
        toast({
          variant: "destructive",
          title: "Erro ao atualizar freelancer",
          description: reportUserError(error, { source: "freelancer-form" }).description,
        });
        return;
      }

      console.log("Freelancer atualizado com sucesso!");

      toast({
        title: "Freelancer atualizado com sucesso",
        description: "As informações foram salvas no banco de dados.",
      });

      // Invalidar queries relacionadas ao freelancer
      invalidateFreelancers();
      
      onSuccess?.();
    } catch (error) {
      console.error("Erro inesperado ao atualizar freelancer:", error);
      toast({
        variant: "destructive",
        title: "Erro inesperado",
        description: "Ocorreu um erro ao salvar as informações.",
      });
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="nome"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nome</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="quality_seal"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox 
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>
                    Selo de Qualidade
                  </FormLabel>
                  <p className="text-sm text-muted-foreground">
                    Marque esta opção para verificar este freelancer com o selo de qualidade
                  </p>
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="is_active"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox 
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>
                    Freelancer Ativo
                  </FormLabel>
                  <p className="text-sm text-muted-foreground">
                    Desmarque se o freelancer estiver inativo ou indisponível
                  </p>
                </div>
              </FormItem>
            )}
          />
        </div>

        {/* Mostrar data de descadastro se existir */}
        {freelancer.unsubscribed_at && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-red-800">
                Freelancer Descadastrado
              </span>
            </div>
            <p className="text-sm text-red-600 mt-1">
              Data de descadastro: {new Date(freelancer.unsubscribed_at).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
        )}

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input {...field} type="email" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="telefone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Telefone</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="cidade"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Endereço</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Cidade</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="state"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Estado</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="country"
            render={({ field }) => (
              <FormItem>
                <FormLabel>País</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
          <FormField
            control={form.control}
            name="pretensao_salarial"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Pretensão Salarial</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="portfolio_url"
            render={({ field }) => (
              <FormItem>
                <FormLabel>URL do Portfolio</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="linkedin_url"
            render={({ field }) => (
              <FormItem>
                <FormLabel>URL do LinkedIn</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
          <FormField
            control={form.control}
            name="historia_profissional"
            render={({ field }) => (
              <FormItem>
                <FormLabel>História Profissional</FormLabel>
                <FormControl>
                  <Textarea {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
          <FormField
            control={form.control}
            name="objetivos"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Objetivos</FormLabel>
                <FormControl>
                  <Textarea {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
          <FormField
            control={form.control}
            name="areas"
            render={({ field }) => {
              // O dado pode chegar como string (legado — texto livre separado por
              // vírgula) ou array. Normaliza em array de strings pro MultiSelect.
              const raw: unknown = field.value;
              const selected: string[] = Array.isArray(raw)
                ? raw
                : typeof raw === "string" && raw
                  ? raw.split(",").map((s) => s.trim()).filter(Boolean)
                  : [];

              return (
                <FormItem>
                  <FormLabel>Áreas de Atuação</FormLabel>
                  <FormControl>
                    <MultiSelect
                      options={areasCanonicas.map((a) => ({ label: a.nome, value: a.nome }))}
                      selected={selected}
                      onChange={(values) => field.onChange(values)}
                      placeholder={
                        areasCanonicas.length === 0
                          ? "Carregando áreas..."
                          : "Selecione uma ou mais áreas"
                      }
                      disabled={areasCanonicas.length === 0}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>

        {/* Additional Information Section */}
        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Informações Adicionais</FormLabel>
              <FormControl>
                <Textarea {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="border rounded-lg p-4 bg-gray-50">
          <Collapsible open={isAdditionalInfoOpen} onOpenChange={setIsAdditionalInfoOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="outline" className="w-full justify-between">
                Informações Adicionais
                <ChevronDown className={`h-4 w-4 transition-transform ${isAdditionalInfoOpen ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-4 mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="phone_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telefone Adicional</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="(11) 99999-9999" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="birth_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Data de Nascimento</FormLabel>
                      <FormControl>
                        <Input {...field} type="date" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="cpf"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CPF</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="000.000.000-00" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="rg"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>RG</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="00.000.000-0" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="zip_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CEP</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="00000-000" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="company_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome da Empresa</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Nome da empresa" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="cnpj"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CNPJ</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="00.000.000/0000-00" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="bank_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome do Banco</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Ex: Banco do Brasil" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="agency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Agência</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="0000" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="account_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Número da Conta</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="00000-0" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="pix_key"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Chave PIX</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="CPF, email, telefone ou chave aleatória" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CollapsibleContent>
          </Collapsible>
        </div>

        <Button type="submit" className="w-full">
          Salvar Alterações
        </Button>
      </form>
    </Form>
  );
}
